"use client"

import { notify } from "@/lib/notifications/notify"
import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { AlertCircle, Check, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { createClient } from "@/lib/supabase/client"
import { Textarea } from "@/components/ui/textarea"
import { BoothMark } from "@/components/ui/booth-mark"
import { SendMark } from "@/components/ui/send-mark"
import { HeroCtaButton } from "@/components/glass/hero-cta"
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetDescription,
  BottomSheetTitle,
} from "@/components/ui/bottom-sheet"

// Modal that handles a barber's "I'm interested" inquiry on a job listing.
// Mirrors the owner-side InquireModal pattern:
//   1. notification → owner   (type: 'job_inquiry')
//   2. get-or-create conversation between barber + owner
//   3. message inserted into the existing messages table
//   4. job_inquiries row inserted (audit trail; status starts 'pending')
// A synchronous ref guard prevents rapid-click double-submits.
//
// Presented in the shared BottomSheet — the Apple Pay shape: it rises from the
// edge the thumb is already at, so the send button lands under the thumb
// instead of halfway up the screen.
//
// On success the sheet holds for a beat on a confirmation before it dismisses:
// closing the instant the insert returns reads as the tap having missed.
//
// From sm up the sheet widens and its type and padding step up with it — at
// 480px on a desktop screen it read as a phone component someone forgot to
// finish.

interface JobInquireModalProps {
  open: boolean
  onClose: () => void
  listingId: string
  listingTitle: string
  shopId: string
  shopName: string
  ownerId: string
  barberId: string
  barberName: string
  onSent?: () => void
}

function defaultMessage(barberName: string, shopName: string, listingTitle: string) {
  return `Hey ${shopName}, I came across your '${listingTitle}' post and I'm interested. Would you be open to chatting? — ${barberName}`
}

export function JobInquireModal({
  open, onClose, listingId, listingTitle, shopId, shopName, ownerId, barberId, barberName, onSent,
}: JobInquireModalProps) {
  const supabase = createClient()
  const [message, setMessage] = useState("")
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sendingRef = useRef(false)

  useEffect(() => {
    if (!open) return
    setSent(false)
    setError(null)
    setMessage(defaultMessage(barberName, shopName, listingTitle))
  }, [open, barberName, shopName, listingTitle])

  const send = async () => {
    if (sendingRef.current) return
    if (!message.trim()) { toast.error("Add a message"); return }
    sendingRef.current = true
    setError(null)
    setSending(true)
    try {
      // The message first, the notification after — and never the other way
      // round. This used to open with notify() and `throw notifErr`, so a
      // notification that didn't post (a dropped request on a phone, a 401 on
      // a stale session) surfaced to the barber as "Couldn't send inquiry"
      // when nothing had been attempted yet. notify() is explicitly
      // non-throwing for this reason; the sheet was overriding that.

      // 1. Get-or-create conversation between barber and owner
      const { data: convo, error: convoSelErr } = await supabase
        .from("conversations")
        .select("id")
        .or(`and(participant_1.eq.${barberId},participant_2.eq.${ownerId}),and(participant_1.eq.${ownerId},participant_2.eq.${barberId})`)
        .maybeSingle()
      if (convoSelErr) throw convoSelErr

      let conversationId = convo?.id
      if (!conversationId) {
        const { data: newConvo, error: newConvoErr } = await supabase
          .from("conversations")
          .insert({ participant_1: barberId, participant_2: ownerId })
          .select("id")
          .single()
        if (newConvoErr) throw newConvoErr
        conversationId = newConvo?.id
      }

      // 2. Message body
      if (conversationId) {
        const { error: msgErr } = await supabase.from("messages").insert({
          conversation_id: conversationId,
          sender_id: barberId,
          receiver_id: ownerId,
          content: message.trim(),
        })
        if (msgErr) throw msgErr
      }

      // 3. job_inquiries audit row (idempotent — UNIQUE(listing_id, barber_id))
      const { error: inqErr } = await supabase.from("job_inquiries").insert({
        listing_id: listingId,
        shop_id: shopId,
        owner_id: ownerId,
        barber_id: barberId,
        message: message.trim(),
        conversation_id: conversationId,
      })
      // 23505 = unique violation. If they already inquired, the message
      // still went through above so we don't surface this as an error.
      if (inqErr && inqErr.code !== "23505") throw inqErr

      // 4. Tell the owner. Best effort: the message is already sent, and a
      // failed notification is not worth telling the barber their inquiry
      // didn't go through.
      const { error: notifErr } = await notify({
        recipient: ownerId,
        type: "job_inquiry",
        jobId: listingId,
        shopId,
        metadata: { listing_id: listingId, shop_id: shopId, barber_id: barberId },
      })
      if (notifErr) console.error("[inquire] owner notification:", notifErr.message)

      // Confirm in place, then dismiss. 1.1s was long enough to be missed
      // entirely — people reported sending an inquiry and seeing nothing
      // happen. The toast still fires for anyone who has already looked away.
      setSent(true)
      toast.success("Inquiry sent")
      onSent?.()
      window.setTimeout(onClose, 2600)
    } catch (err: any) {
      // In the sheet as well as the toast: a toast on a phone is gone before
      // you look up, and "it said failed" with no reason is unreportable.
      const message = err?.message || "Couldn't send inquiry"
      setError(message)
      toast.error(message)
    } finally {
      sendingRef.current = false
      setSending(false)
    }
  }

  return (
    <BottomSheet open={open} onOpenChange={(o) => { if (!o && !sending) onClose() }}>
      <BottomSheetContent closeDisabled={sending} className="sm:max-w-[576px]">
          {sent ? (
            /* Confirmation. Same sheet, same height band — nothing jumps. */
            <div className="flex flex-col items-center gap-3 px-6 pb-10 pt-10 text-center sm:px-8 sm:pb-12 sm:pt-12">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30">
                <Check className="h-6 w-6 text-emerald-400" strokeWidth={2} />
              </span>
              <BottomSheetTitle className="text-base font-medium">Inquiry sent</BottomSheetTitle>
              <BottomSheetDescription className="max-w-[34ch] text-sm leading-relaxed text-white/50">
                {shopName} can reply in your messages.
              </BottomSheetDescription>
              <Link
                href="/dashboard/messages"
                className="mt-1 text-[13px] font-semibold text-white underline-offset-4 hover:underline"
              >
                Go to messages
              </Link>
            </div>
          ) : (
            <>
              <div className="px-6 pb-5 pt-3 sm:px-8 sm:pb-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/40">
                  Inquire
                </p>
                {/* The mark says who carries this message: the barber is not
                    emailing the shop, they are reaching them through BOOTH. */}
                <div className="mt-1.5 flex items-center gap-2.5">
                  <BoothMark size={26} className="shrink-0 text-white/45" />
                  <BottomSheetTitle className="text-xl font-medium leading-snug tracking-tight sm:text-[22px]">
                    {listingTitle}
                  </BottomSheetTitle>
                </div>
                <BottomSheetDescription className="mt-1 text-sm text-white/50">
                  {shopName}
                </BottomSheetDescription>
              </div>

              <div className="px-6 sm:px-8">
                <label htmlFor="job-inquire-message" className="sr-only">
                  Your message
                </label>
                <Textarea
                  id="job-inquire-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  maxLength={1000}
                  disabled={sending}
                  className="resize-none rounded-2xl border-white/10 bg-white/[0.04] text-sm leading-relaxed text-white placeholder:text-white/35 focus-visible:border-white/25 focus-visible:ring-1 focus-visible:ring-white/20"
                  placeholder="A short note about your experience and why you're interested…"
                />
                <p className="mt-2 text-right text-[11px] tabular-nums text-white/30">
                  {message.length}/1000
                </p>
                {error && (
                  <p className="flex items-start gap-1.5 text-[12px] leading-relaxed text-red-400">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {error}
                  </p>
                )}
              </div>

              {/* Single full-width commit, thumb-height, at the bottom edge —
                  the one thing this sheet exists to do. Cancel is the X and
                  the backdrop; a second button here would only compete. */}
              <div className="px-6 pb-6 pt-4 sm:px-8 sm:pb-8">
                {/* The site CTA, not a local one — same pill, gloss and sheen
                    as the landing and signup buttons. invert is pinned because
                    this sheet is dark at every theme, and the --pg-* fallback
                    would hand a light-mode viewer a black pill on near-black. */}
                <HeroCtaButton
                  onClick={send}
                  disabled={sending || !message.trim()}
                  invert={{ bg: "#FFFFFF", fg: "#0C0C0C" }}
                  className="sm:h-[52px] sm:text-base"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <SendMark size={16} />
                  )}
                  Send inquiry
                </HeroCtaButton>
              </div>
            </>
          )}
      </BottomSheetContent>
    </BottomSheet>
  )
}
